import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-dive-black py-6 pb-24" style={{ paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-dive-neon" />
            <h1 className="text-3xl font-display text-white tracking-wide">Terms of Service</h1>
          </div>
          
          <p className="text-gray-400 text-sm">Last updated: March 4, 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-sm max-w-none">
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-6 mb-6">
            <p className="text-gray-300 leading-relaxed">
              Welcome to Tilted ("App", "Service"). These Terms of Service ("Terms") govern your use of the Tilted mobile application and website operated by Tilted ("we", "us", or "our"). By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">1. Eligibility</h2>
            <p className="text-gray-300 leading-relaxed">
              You must be at least 21 years of age to use Tilted. By creating an account, you represent and warrant that you are at least 21 years old. We may terminate accounts of users found to be under 21.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">2. User Accounts</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate information and to keep it updated. You are responsible for all activity under your account.
            </p>
            <p className="text-gray-300 leading-relaxed">
              You may delete your account at any time from your Profile settings. Deletion is permanent and irreversible.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">3. User-Generated Content</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Tilted allows users to post ratings, reviews, comments, photos, and other content ("User Content"). You retain ownership of your User Content, but by posting it, you grant us a non-exclusive, worldwide, royalty-free license to display, distribute, and use your User Content in connection with the Service.
            </p>
            <p className="text-gray-300 leading-relaxed">
              You are solely responsible for the User Content you post. You represent that you have the right to post it and that it does not violate these Terms or any applicable laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">4. Community Standards &amp; Prohibited Content</h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <p className="text-red-400 font-medium">
                Tilted has zero tolerance for objectionable content and abusive behavior.
              </p>
            </div>
            <p className="text-gray-300 leading-relaxed mb-4">
              You agree NOT to post, upload, or share content that:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
              <li>Is hateful, threatening, harassing, discriminatory, or promotes violence</li>
              <li>Contains sexually explicit or pornographic material</li>
              <li>Is defamatory, libelous, or invades another person's privacy</li>
              <li>Contains spam, scams, or misleading information</li>
              <li>Promotes illegal activities or substances</li>
              <li>Infringes on intellectual property or other proprietary rights</li>
              <li>Contains profanity, slurs, or language intended to demean others</li>
              <li>Impersonates another person or entity</li>
              <li>Is otherwise objectionable or inappropriate for a community-focused platform</li>
            </ul>
            <p className="text-gray-300 leading-relaxed">
              We reserve the right to remove any content that violates these standards and to suspend or permanently ban users who violate them, at our sole discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">5. Reporting &amp; Blocking</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you encounter content or behavior that violates these Terms, you can report it using the report feature available throughout the app. We review all reports and will take action within 24 hours, which may include removing content and banning the offending user.
            </p>
            <p className="text-gray-300 leading-relaxed">
              You may block other users at any time. Blocking a user will hide their content from your feed and prevent them from interacting with your content. Blocking also notifies us so we can review the blocked user's activity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">6. Content Moderation</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We employ automated content filtering and manual review to maintain community standards. Content that is flagged by our automated systems or reported by users will be reviewed and may be removed.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Users who repeatedly violate community standards will have their accounts suspended or permanently terminated. We act on reports of objectionable content within 24 hours.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">7. Intellectual Property</h2>
            <p className="text-gray-300 leading-relaxed">
              The Tilted name, logo, and all associated branding, design, and software are the property of Tilted and are protected by intellectual property laws. You may not copy, modify, or distribute any part of the Service without our written consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">8. Disclaimer of Warranties</h2>
            <p className="text-gray-300 leading-relaxed">
              The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or secure. Bar ratings and reviews reflect individual user opinions and do not constitute endorsements by Tilted.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">9. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed">
              To the fullest extent permitted by law, Tilted shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Service. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim, if any.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">10. Termination</h2>
            <p className="text-gray-300 leading-relaxed">
              We may suspend or terminate your account at any time for any reason, including violation of these Terms. You may also delete your account at any time. Upon termination, your right to use the Service ceases immediately, and all User Content associated with your account may be deleted.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">11. Changes to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update these Terms from time to time. If we make material changes, we will notify you through the App or by other means. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">12. Governing Law</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms are governed by and construed in accordance with the laws of the State of Illinois, without regard to its conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-display text-white mb-4 border-b border-dive-gray pb-2">13. Contact</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have questions about these Terms or need to report a violation, contact us at:
            </p>
            <p className="mb-4">
              <a href="mailto:gettiltedapp@gmail.com" className="text-dive-neon hover:underline">gettiltedapp@gmail.com</a>
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-dive-gray pt-6 mt-8">
            <p className="text-gray-500 text-sm text-center">
              This document was last updated on March 4, 2026
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
